import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import { AuthModal } from './AuthComponents';
import { ProfileModal } from './ProfileModal';

const API_BASE = 'http://174.138.79.56:3001';

// ─── Design tokens ────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,600;1,9..144,300&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink:       #1a1a2e;
    --paper:     #f5f0e8;
    --cream:     #ede8dc;
    --accent:    #c84b31;
    --gold:      #d4a843;
    --sage:      #4a7c59;
    --muted:     #8a8070;
    --border:    #d0c8b8;
    --panel:     #faf7f2;
    --shadow:    0 2px 12px rgba(26,26,46,0.08);
    --shadow-lg: 0 8px 32px rgba(26,26,46,0.14);
    --radius:    6px;
    --mono:      'DM Mono', monospace;
    --serif:     'Fraunces', serif;
  }

  html, body, #root {
    height: 100%;
    background: var(--paper);
    color: var(--ink);
    font-family: var(--mono);
    font-size: 13px;
  }

  .app {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }

  /* ── Header ── */
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    height: 48px;
    background: var(--ink);
    color: var(--paper);
    flex-shrink: 0;
    border-bottom: 2px solid var(--accent);
    z-index: 100;
  }
  .header-title {
    font-family: var(--serif);
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.3px;
    color: var(--paper);
  }
  .header-title span { color: var(--gold); }
  .header-meta {
    font-size: 11px;
    color: var(--muted);
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .header-count {
    background: var(--accent);
    color: white;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 500;
  }

  /* ── Main layout ── */
  .main {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  /* ── Map ── */
  .map-container {
    flex: 1;
    position: relative;
    min-width: 0;
  }
  #map { width: 100%; height: 100%; }

  /* ── Right panel ── */
  .panel {
    width: 340px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    background: var(--panel);
    border-left: 1px solid var(--border);
    overflow: hidden;
  }

  /* ── Filter section ── */
  .filter-section {
    border-bottom: 1px solid var(--border);
    overflow: hidden;
    transition: max-height 0.3s ease;
  }
  .filter-section.collapsed { max-height: 40px; }
  .filter-section.expanded  { max-height: 500px; }

  .filter-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    cursor: pointer;
    background: var(--cream);
    border: none;
    width: 100%;
    text-align: left;
    font-family: var(--mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .filter-toggle:hover { background: var(--border); }
  .filter-toggle-arrow { transition: transform 0.2s; }
  .filter-toggle-arrow.open { transform: rotate(180deg); }

  .filter-body { padding: 12px 16px 16px; }

  .filter-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 8px;
  }
  .filter-row.full { grid-template-columns: 1fr; }

  .field label {
    display: block;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 4px;
  }
  .field input, .field select {
    width: 100%;
    padding: 7px 10px;
    background: white;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    font-family: var(--mono);
    font-size: 12px;
    color: var(--ink);
    outline: none;
    transition: border-color 0.15s;
  }
  .field input:focus, .field select:focus {
    border-color: var(--accent);
  }

  .btn {
    width: 100%;
    padding: 9px;
    background: var(--ink);
    color: var(--paper);
    border: none;
    border-radius: var(--radius);
    font-family: var(--mono);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    margin-top: 10px;
    transition: background 0.15s;
  }
  .btn:hover { background: var(--accent); }
  .btn.secondary {
    background: transparent;
    color: var(--ink);
    border: 1px solid var(--border);
    margin-top: 6px;
  }
  .btn.secondary:hover { background: var(--cream); }

  /* ── Card section ── */
  .card-section {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    color: var(--muted);
    gap: 8px;
    padding: 20px;
  }
  .empty-state-icon { font-size: 32px; margin-bottom: 4px; }
  .empty-state-title {
    font-family: var(--serif);
    font-size: 16px;
    font-weight: 300;
    color: var(--ink);
  }
  .empty-state-sub { font-size: 12px; line-height: 1.5; }

  /* ── Lottery card ── */
  .lottery-card {
    background: white;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }

  .card-header {
    padding: 14px 16px 10px;
    border-bottom: 1px solid var(--cream);
  }
  .card-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--sage);
    margin-bottom: 6px;
  }
  .card-tag.sale { color: var(--gold); }
  .card-title {
    font-family: var(--serif);
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
    line-height: 1.3;
    margin-bottom: 4px;
  }
  .card-address {
    font-size: 11px;
    color: var(--muted);
  }

  .card-meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    background: var(--cream);
    border-bottom: 1px solid var(--cream);
  }
  .card-meta-item {
    background: white;
    padding: 10px 14px;
  }
  .card-meta-label {
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 3px;
  }
  .card-meta-value {
    font-size: 13px;
    font-weight: 500;
    color: var(--ink);
  }
  .card-meta-value.urgent { color: var(--accent); }

  /* ── Eligible units ── */
  .units-section { padding: 14px 16px; }
  .units-title {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 10px;
  }
  .unit-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid var(--cream);
  }
  .unit-row:last-child { border-bottom: none; }
  .unit-left { display: flex; flex-direction: column; gap: 2px; }
  .unit-size {
    font-size: 12px;
    font-weight: 500;
    color: var(--ink);
  }
  .unit-ami {
    font-size: 10px;
    color: var(--muted);
  }
  .unit-right { text-align: right; }
  .unit-rent {
    font-size: 14px;
    font-weight: 500;
    color: var(--sage);
    font-family: var(--serif);
  }
  .unit-income {
    font-size: 10px;
    color: var(--muted);
  }

  .card-footer { padding: 12px 16px; border-top: 1px solid var(--cream); }
  .apply-btn {
    display: block;
    width: 100%;
    padding: 10px;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: var(--radius);
    font-family: var(--mono);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    text-align: center;
    text-decoration: none;
    transition: opacity 0.15s;
  }
  .apply-btn:hover { opacity: 0.85; }
  .back-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    font-family: var(--mono);
    font-size: 11px;
    color: var(--muted);
    cursor: pointer;
    padding: 0 0 10px;
  }
  .back-btn:hover { color: var(--ink); }

  /* ── Loading overlay ── */
  .map-loading {
    position: absolute;
    top: 12px;
    left: 12px;
    background: var(--ink);
    color: var(--paper);
    padding: 6px 12px;
    border-radius: var(--radius);
    font-size: 11px;
    z-index: 500;
  }

  /* ── Auth header ── */
  .auth-btn {
    padding: 5px 12px;
    border-radius: var(--radius);
    font-family: var(--mono);
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: opacity 0.15s;
  }
  .auth-btn:hover { opacity: 0.8; }
  .auth-btn.login  { background: var(--accent); color: white; }
  .auth-btn.logout { background: transparent; color: var(--muted); border: 1px solid var(--muted); }
  .auth-user {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px; color: var(--paper);
  }
  .auth-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: var(--sage);
  }

  /* ── Profile toggle ── */
  .profile-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: var(--cream);
    border-bottom: 1px solid var(--border);
    font-size: 11px;
  }
  .profile-toggle {
    display: flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    color: var(--ink);
    font-weight: 500;
  }
  .toggle-switch {
    width: 30px; height: 16px;
    background: var(--border);
    border-radius: 8px;
    position: relative;
    transition: background 0.2s;
    flex-shrink: 0;
  }
  .toggle-switch.on { background: var(--sage); }
  .toggle-knob {
    position: absolute;
    top: 2px; left: 2px;
    width: 12px; height: 12px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s;
  }
  .toggle-switch.on .toggle-knob { left: 16px; }
  .save-profile-btn {
    background: none;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 3px 9px;
    font-family: var(--mono);
    font-size: 10px;
    color: var(--muted);
    cursor: pointer;
    transition: all 0.15s;
  }
  .save-profile-btn:hover { border-color: var(--sage); color: var(--sage); }
  .save-profile-btn.saved { border-color: var(--sage); color: var(--sage); }

  /* ── Scrollbar ── */
  .card-section::-webkit-scrollbar { width: 4px; }
  .card-section::-webkit-scrollbar-track { background: transparent; }
  .card-section::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

// ─── Helpers ──────────────────────────────────────────────────
function formatCurrency(n) {
  if (!n) return '—';
  return '$' + Number(n).toLocaleString();
}

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  const diff = Math.ceil((date - new Date()) / 86400000);
  const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (diff <= 7) return `${label} (${diff}d left)`;
  return label;
}

function daysUntil(d) {
  if (!d) return 999;
  return Math.ceil((new Date(d) - new Date()) / 86400000);
}

function unitLayoutLabel(id) {
  return ['Studio', '1 Bedroom', '2 Bedroom', '3 Bedroom', '4 Bedroom'][id - 1] || `${id - 1}BR`;
}

// ─── Filter Panel ─────────────────────────────────────────────
function FilterPanel({ filters, setFilters, onSearch, filterOptions, loading, resultCount, isAuthenticated, profile, onUseProfile, usingProfile, onSaveProfile, profileSaved, onOpenProfile }) {
  const [open, setOpen] = useState(true);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  return (
    <div className={`filter-section ${open ? 'expanded' : 'collapsed'}`}>
      <button className="filter-toggle" onClick={() => setOpen(o => !o)}>
        <span>Filters {resultCount !== null ? `· ${resultCount} results` : ''}</span>
        <span className={`filter-toggle-arrow ${open ? 'open' : ''}`}>▼</span>
      </button>

      {/* Profile bar — only shown when logged in */}
      {isAuthenticated && (
        <div className="profile-bar">
          {profile ? (
            <>
              <label className="profile-toggle" onClick={onUseProfile}>
                <div className={`toggle-switch ${usingProfile ? 'on' : ''}`}>
                  <div className="toggle-knob" />
                </div>
                Use my profile
              </label>
              <button
                className="save-profile-btn"
                onClick={onOpenProfile}
              >
                Edit profile
              </button>
            </>
          ) : (
            <button
              className="save-profile-btn"
              style={{ width: '100%', textAlign: 'center', padding: '4px' }}
              onClick={onOpenProfile}
            >
              + Create Household Profile
            </button>
          )}
        </div>
      )}

      <div className="filter-body">
        <div className="filter-row">
          <div className="field">
            <label>Annual Income</label>
            <input
              type="number"
              placeholder="e.g. 75000"
              value={filters.income}
              onChange={e => set('income', e.target.value)}
            />
          </div>
          <div className="field">
            <label>Household Size</label>
            <select value={filters.household_size} onChange={e => set('household_size', e.target.value)}>
              <option value="">Any</option>
              {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>)}
            </select>
          </div>
        </div>

        <div className="filter-row">
          <div className="field">
            <label>Min Bedrooms</label>
            <select value={filters.bedrooms} onChange={e => set('bedrooms', e.target.value)}>
              <option value="">Any</option>
              <option value="0">Studio</option>
              <option value="1">1 BR+</option>
              <option value="2">2 BR+</option>
              <option value="3">3 BR+</option>
            </select>
          </div>
          <div className="field">
            <label>Max Rent / mo</label>
            <input
              type="number"
              placeholder="e.g. 2000"
              value={filters.max_rent}
              onChange={e => set('max_rent', e.target.value)}
            />
          </div>
        </div>

        <div className="filter-row">
          <div className="field">
            <label>Borough</label>
            <select value={filters.borough} onChange={e => set('borough', e.target.value)}>
              <option value="">All boroughs</option>
              {filterOptions.boroughs.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Type</label>
            <select value={filters.listing_type} onChange={e => set('listing_type', e.target.value)}>
              <option value="">Both</option>
              <option value="rental">Rental</option>
              <option value="sale">Sale</option>
            </select>
          </div>
        </div>

        <div className="filter-row full">
          <div className="field">
            <label>Neighborhood</label>
            <select value={filters.neighborhood} onChange={e => set('neighborhood', e.target.value)}>
              <option value="">All neighborhoods</option>
              {filterOptions.neighborhoods.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        <button className="btn" onClick={onSearch} disabled={loading}>
          {loading ? 'Searching...' : 'Search Lotteries'}
        </button>
        <button className="btn secondary" onClick={() => {
          setFilters({ income: '', household_size: '', bedrooms: '', max_rent: '', borough: '', neighborhood: '', listing_type: '' });
        }}>
          Clear Filters
        </button>
      </div>
    </div>
  );
}

// ─── Lottery Card ─────────────────────────────────────────────
function LotteryCard({ lottery, onBack }) {
  const units = lottery.eligible_units || [];
  const urgent = daysUntil(lottery.end_date) <= 7;

  // Group units by size for display
  const bySize = {};
  units.forEach(u => {
    const key = `${u.unit_layout_id}-${u.ami}`;
    if (!bySize[key]) bySize[key] = u;
  });
  const displayUnits = Object.values(bySize);

  const applyUrl = lottery.apply_url ||
    `https://housingconnect.nyc.gov/PublicWeb/details/${lottery.lottery_id}`;

  return (
    <div className="lottery-card">
      <div className="card-header">
        {onBack && (
          <button className="back-btn" onClick={onBack}>
            ← Back
          </button>
        )}
        <div className={`card-tag ${lottery.listing_type === 'sale' ? 'sale' : ''}`}>
          {lottery.listing_type === 'sale' ? '🏠 For Sale' : '🔑 Rental'} · {lottery.borough}
        </div>
        <div className="card-title">{lottery.lottery_name}</div>
        <div className="card-address">{lottery.address}{lottery.zip ? `, ${lottery.zip}` : ''}</div>
      </div>

      <div className="card-meta">
        <div className="card-meta-item">
          <div className="card-meta-label">Deadline</div>
          <div className={`card-meta-value ${urgent ? 'urgent' : ''}`}>
            {formatDate(lottery.end_date)}
          </div>
        </div>
        <div className="card-meta-item">
          <div className="card-meta-label">Total Units</div>
          <div className="card-meta-value">{lottery.total_units || '—'}</div>
        </div>
        <div className="card-meta-item">
          <div className="card-meta-label">Neighborhood</div>
          <div className="card-meta-value" style={{ fontSize: 11 }}>{lottery.neighborhood || '—'}</div>
        </div>
        <div className="card-meta-item">
          <div className="card-meta-label">Income Range</div>
          <div className="card-meta-value" style={{ fontSize: 11 }}>
            {formatCurrency(lottery.min_income)}–{formatCurrency(lottery.max_income)}
          </div>
        </div>
      </div>

      {displayUnits.length > 0 && (
        <div className="units-section">
          <div className="units-title">
            {units.length > 0 ? 'Eligible Units' : 'Available Units'}
          </div>
          {displayUnits.map((u, i) => (
            <div className="unit-row" key={i}>
              <div className="unit-left">
                <div className="unit-size">{unitLayoutLabel(u.unit_layout_id)}</div>
                <div className="unit-ami">{u.ami}</div>
              </div>
              <div className="unit-right">
                <div className="unit-rent">{formatCurrency(u.monthly_rent)}/mo</div>
                <div className="unit-income">
                  {formatCurrency(u.min_income)}–{formatCurrency(u.max_income)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card-footer">
        <a className="apply-btn" href={applyUrl} target="_blank" rel="noopener noreferrer">
          Apply on Housing Connect →
        </a>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────
function HousingApp() {
  const { user, token, isAuthenticated, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [profile, setProfile]             = useState(null);
  const [usingProfile, setUsingProfile]   = useState(false);
  const [profileSaved, setProfileSaved]   = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const mapRef       = useRef(null);
  const leafletRef   = useRef(null);
  const layerRef     = useRef(null);
  const markerMapRef = useRef(new Map()); // lottery_id -> leaflet marker

  const [lotteries, setLotteries]         = useState([]);
  const [selected, setSelected]           = useState(null);
  const [loading, setLoading]             = useState(false);
  const [filterOptions, setFilterOptions] = useState({ boroughs: [], neighborhoods: [] });
  const [filters, setFilters]             = useState({
    income: '', household_size: '', bedrooms: '',
    max_rent: '', borough: '', neighborhood: '', listing_type: '',
  });

  // ── Init map ──
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      const L = window.L;
      const map = L.map(mapRef.current, { zoomControl: false }).setView([40.72, -73.98], 11);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap © Carto',
        maxZoom: 19,
      }).addTo(map);
      L.control.zoom({ position: 'bottomright' }).addTo(map);
      layerRef.current = L.layerGroup().addTo(map);
      leafletRef.current = map;
      fetchLotteries();
    };
    document.head.appendChild(script);

    return () => { leafletRef.current?.remove(); };
  }, []);

  // ── Fetch filter options ──
  useEffect(() => {
    fetch(`${API_BASE}/api/filters`)
      .then(r => r.json())
      .then(setFilterOptions)
      .catch(console.error);
  }, []);

  // ── Fetch user profile when logged in ──
  useEffect(() => {
    if (!isAuthenticated || !token) { setProfile(null); setUsingProfile(false); return; }
    fetch(`${API_BASE}/api/profile`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(p => { if (p) setProfile(p); })
      .catch(console.error);
  }, [isAuthenticated, token]);

  // ── Toggle use profile ──
  const handleUseProfile = () => {
    if (!profile) return;
    if (!usingProfile) {
      // Load profile values into filters
      setFilters({
        income:         profile.annual_income   ? String(profile.annual_income)   : '',
        household_size: profile.household_size  ? String(profile.household_size)  : '',
        bedrooms:       profile.bedroom_types?.length ? String(Math.min(...profile.bedroom_types)) : '',
        max_rent:       '',
        borough:        profile.boroughs?.[0]       || '',
        neighborhood:   profile.neighborhoods?.[0]  || '',
        listing_type:   profile.listing_types?.[0]  || '',
      });
    }
    setUsingProfile(v => !v);
  };

  // ── Save current filters as profile ──
  const handleSaveProfile = async () => {
    if (!token) return;
    try {
      const bedroomVal = filters.bedrooms !== '' ? [parseInt(filters.bedrooms)] : null;
      await fetch(`${API_BASE}/api/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          annual_income:  filters.income         ? parseFloat(filters.income)         : null,
          household_size: filters.household_size ? parseInt(filters.household_size)   : null,
          bedroom_types:  bedroomVal,
          boroughs:       filters.borough        ? [filters.borough]        : null,
          neighborhoods:  filters.neighborhood   ? [filters.neighborhood]   : null,
          listing_types:  filters.listing_type   ? [filters.listing_type]   : null,
        })
      });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
    } catch (err) {
      console.error('Save profile error:', err);
    }
  };

  // ── Fetch lotteries ──
  const fetchLotteries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.income)        params.set('income', filters.income);
      if (filters.household_size) params.set('household_size', filters.household_size);
      if (filters.bedrooms !== '') params.set('bedrooms', filters.bedrooms);
      if (filters.borough)       params.set('borough', filters.borough);
      if (filters.neighborhood)  params.set('neighborhood', filters.neighborhood);
      if (filters.listing_type)  params.set('listing_type', filters.listing_type);

      const res  = await fetch(`${API_BASE}/api/lotteries?${params}`);
      let data   = await res.json();

      // Client-side max_rent filter (applied after eligibility filter)
      if (filters.max_rent) {
        const maxR = parseFloat(filters.max_rent);
        data = data.filter(l => {
          const units = l.eligible_units || [];
          if (units.length === 0) return true;
          return units.some(u => u.monthly_rent <= maxR);
        });
      }

      setLotteries(data);
      renderMarkers(data);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // ── Render map markers ──
  const renderMarkers = useCallback((data) => {
    if (!leafletRef.current || !window.L) return;
    const L = window.L;
    layerRef.current.clearLayers();
    markerMapRef.current.clear();

    data.forEach(lottery => {
      const lat = parseFloat(lottery.latitude);
      const lng = parseFloat(lottery.longitude);
      if (!lat || !lng) return;

      const marker = L.circleMarker([lat, lng], {
        radius: 8,
        fillColor: '#c84b31',
        color: 'white',
        weight: 2,
        fillOpacity: 0.85,
      }).addTo(layerRef.current);

      marker.on('click', () => {
        setSelected(lottery);
        leafletRef.current.setView([lat, lng], 14, { animate: true });
        // Highlight selected
        markerMapRef.current.forEach((m, id) => {
          m.setStyle({ fillColor: id === lottery.lottery_id ? '#1a1a2e' : '#c84b31', radius: id === lottery.lottery_id ? 11 : 8 });
        });
      });

      markerMapRef.current.set(lottery.lottery_id, marker);
    });
  }, []);

  // ── Update marker highlight when selected changes ──
  useEffect(() => {
    markerMapRef.current.forEach((m, id) => {
      const isSelected = selected && id === selected.lottery_id;
      m.setStyle({
        fillColor: isSelected ? '#1a1a2e' : '#c84b31',
        radius:    isSelected ? 11 : 8,
      });
    });
  }, [selected]);

  return (
    <>
      <style>{css}</style>
      <div className="app">
        {/* Header */}
        <header className="header">
          <div className="header-title">NYC <span>Housing</span> Connect</div>
          <div className="header-meta">
            {lotteries.length > 0 && (
              <span className="header-count">{lotteries.length} active lotteries</span>
            )}
            {isAuthenticated ? (
              <div className="auth-user">
                <div className="auth-dot" />
                <span>{user.username}</span>
                <button className="auth-btn logout" onClick={logout}>Logout</button>
              </div>
            ) : (
              <button className="auth-btn login" onClick={() => setShowAuthModal(true)}>
                Login / Register
              </button>
            )}
          </div>
        </header>

        <div className="main">
          {/* Map */}
          <div className="map-container">
            <div id="map" ref={mapRef} />
            {loading && <div className="map-loading">Loading...</div>}
          </div>

          {/* Right panel */}
          <aside className="panel">
            <FilterPanel
              filters={filters}
              setFilters={setFilters}
              onSearch={fetchLotteries}
              filterOptions={filterOptions}
              loading={loading}
              resultCount={lotteries.length > 0 ? lotteries.length : null}
              isAuthenticated={isAuthenticated}
              profile={profile}
              onUseProfile={handleUseProfile}
              usingProfile={usingProfile}
              onSaveProfile={handleSaveProfile}
              profileSaved={profileSaved}
              onOpenProfile={() => setShowProfileModal(true)}
            />

            <div className="card-section">
              {selected ? (
                <LotteryCard
                  lottery={selected}
                  onBack={() => setSelected(null)}
                />
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">🗺</div>
                  <div className="empty-state-title">Select a lottery</div>
                  <div className="empty-state-sub">
                    Click any marker on the map to view lottery details and eligible units.
                    {lotteries.length === 0 && ' Use filters above and click Search to load lotteries.'}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>

        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
        <ProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          profile={profile}
          onSaved={(saved) => { setProfile(saved); setUsingProfile(false); }}
          filterOptions={filterOptions}
        />
      </div>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <HousingApp />
    </AuthProvider>
  );
}
